import { SearchOutlined } from '@ant-design/icons';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppGlobStore } from 'src/appGlobalStore';
import { RoutePath } from 'src/data/Routes';
//import 'src/styles/_variables.less'
import SearchModal from './SearchModal';

const SearchBar = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const searchIconRef = useRef<HTMLDivElement | null>(null);
  const previousKeysRef = useRef<string[]>([]);
  const location = useLocation();
  const isSearchRoute: boolean = location.pathname.startsWith(RoutePath.SEARCH);

  const syncSearchSelection = useCallback((isOpen: boolean) => {
    useAppGlobStore.setState((state) => {
      const filteredKeys: string[] = state.topMenuSelectedKeys.filter((key) => key !== RoutePath.SEARCH);
      if (isOpen) {
        previousKeysRef.current = filteredKeys;
        return { topMenuSelectedKeys: [RoutePath.SEARCH] };
      }
      if (location.pathname.startsWith(RoutePath.SEARCH)) {
        if (filteredKeys.length) {
          previousKeysRef.current = filteredKeys;
        }
        return { topMenuSelectedKeys: [RoutePath.SEARCH] };
      }
      const fallbackKeys: string[] = previousKeysRef.current.length ? previousKeysRef.current : filteredKeys;
      return { topMenuSelectedKeys: fallbackKeys };
    });
  }, [location.pathname]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setModalOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleClickIcon = () => {
    setModalOpen(!modalOpen);
  };

  const iconClassName: string = modalOpen || isSearchRoute ? 'xm-search-icon xm-search-icon--active' : 'xm-search-icon';

  useEffect(() => {
    syncSearchSelection(modalOpen);
    if (!modalOpen) {
      searchIconRef.current?.blur();
    }
  }, [modalOpen, syncSearchSelection]);

  useEffect(() => {
    if (isSearchRoute) {
      syncSearchSelection(false);
    }
  }, [isSearchRoute, syncSearchSelection]);

  useEffect(() => {
    return () => {
      syncSearchSelection(false);
    };
  }, [syncSearchSelection]);

  return (
    <>
      <div className='xm-search-content-wrapper' tabIndex={0} role="search" aria-label="Search">
        <SearchIcon key='search-show' className={iconClassName} onClick={handleClickIcon} ref={searchIconRef} data-testid='search-trigger' />
      </div>
      <SearchModal open={modalOpen} onClose={handleModalClose} />
    </>
  );
};

const SearchIcon = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function SearchIcon(props, ref) {
    return (
      <div {...props} ref={ref}>
        <SearchOutlined />
      </div>
    );
  }
);

export default SearchBar;
